import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * AutoLogoutTest component for testing auto logout functionality
 * This component should only be used in development/testing environments
 */
export default function AutoLogoutTest() {
  const { user, logout } = useAuth();
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isTestMode, setIsTestMode] = useState(false);

  const addTestResult = (result: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${result}`]);
  };

  const testAutoLogout = () => {
    if (!user) {
      addTestResult('❌ No user logged in - cannot test auto logout');
      return;
    }

    setIsTestMode(true);
    addTestResult('🧪 Starting auto logout test...');
    
    // Test 1: Simulate inactivity
    addTestResult('⏱️ Test 1: Simulating user activity...');
    simulateUserActivity();
    addTestResult('✅ Activity simulation completed');
    
    // Test 2: Simulate tab visibility change
    addTestResult('👁️ Test 2: Simulating tab visibility change...');
    try {
      Object.defineProperty(document, 'hidden', {
        writable: true,
        value: true
      });
      document.dispatchEvent(new Event('visibilitychange'));
      
      // Reset visibility
      setTimeout(() => {
        Object.defineProperty(document, 'hidden', {
          writable: true,
          value: false
        });
        document.dispatchEvent(new Event('visibilitychange'));
        addTestResult('✅ Tab visibility test completed');
      }, 1000);
    } catch (error) {
      addTestResult('⚠️ Tab visibility test failed (browser restriction)');
    }

    // Test 3: Simulate beforeunload event
    addTestResult('🚪 Test 3: Simulating page unload...');
    try {
      const beforeUnloadEvent = new Event('beforeunload');
      window.dispatchEvent(beforeUnloadEvent);
      addTestResult('✅ Page unload test completed');
    } catch (error) {
      addTestResult('⚠️ Page unload test failed (browser restriction)');
    }

    // Test 4: Simulate pagehide event
    addTestResult('📱 Test 4: Simulating page hide...');
    try {
      const pageHideEvent = new Event('pagehide');
      window.dispatchEvent(pageHideEvent);
      addTestResult('✅ Page hide test completed');
    } catch (error) {
      addTestResult('⚠️ Page hide test failed (browser restriction)');
    }

    // Test 5: Simulate online/offline status
    addTestResult('🌐 Test 5: Simulating offline status...');
    try {
      Object.defineProperty(navigator, 'onLine', {
        writable: true,
        value: false
      });
      window.dispatchEvent(new Event('offline'));
      
      setTimeout(() => {
        Object.defineProperty(navigator, 'onLine', {
          writable: true,
          value: true
        });
        window.dispatchEvent(new Event('online'));
        addTestResult('✅ Online/offline test completed');
      }, 1000);
    } catch (error) {
      addTestResult('⚠️ Online/offline test failed (browser restriction)');
    }

    addTestResult('🎉 All tests completed! Check console for detailed logs.');
    setIsTestMode(false);
  };

  const clearTestResults = () => {
    setTestResults([]);
  };

  const simulateUserActivity = () => {
    // Simulate various user activities
    const events = ['mousedown', 'mousemove', 'keypress', 'click', 'keydown'];
    events.forEach(event => {
      document.dispatchEvent(new Event(event));
    });
    addTestResult('👆 Simulated user activity - timers should reset');
  };

  const forceLogout = () => {
    addTestResult('🔓 Forcing logout...');
    logout();
  };

  useEffect(() => {
    if (user) {
      addTestResult(`✅ User logged in: ${user.email}`);
    } else {
      addTestResult('❌ No user logged in');
    }
  }, [user]);

  // Only show this component in development
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  return (
    <Card className="w-full max-w-2xl mx-auto mt-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🧪 Auto Logout Test Panel
          <Badge variant={user ? "default" : "destructive"}>
            {user ? "Logged In" : "Not Logged In"}
          </Badge>
        </CardTitle>
        <CardDescription>
          Test the auto logout functionality. This panel is only visible in development mode.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={testAutoLogout} 
            disabled={!user || isTestMode}
            variant="default"
          >
            {isTestMode ? "Testing..." : "Run Auto Logout Tests"}
          </Button>
          <Button 
            onClick={simulateUserActivity} 
            disabled={!user}
            variant="outline"
          >
            Simulate User Activity
          </Button>
          <Button 
            onClick={forceLogout} 
            disabled={!user}
            variant="destructive"
          >
            Force Logout
          </Button>
          <Button 
            onClick={clearTestResults} 
            variant="outline"
          >
            Clear Results
          </Button>
        </div>

        <div className="space-y-2">
          <h4 className="font-medium">Test Results:</h4>
          <div className="bg-gray-100 p-4 rounded-md max-h-64 overflow-y-auto">
            {testResults.length === 0 ? (
              <p className="text-gray-500 text-sm">No test results yet...</p>
            ) : (
              <div className="space-y-1">
                {testResults.map((result, index) => (
                  <div key={index} className="text-sm font-mono">
                    {result}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="text-xs text-gray-500 space-y-1">
          <p><strong>Test Scenarios:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Inactivity timeout (30 minutes)</li>
            <li>Tab visibility change (5 minutes when hidden)</li>
            <li>Page unload (browser close, tab close)</li>
            <li>Page hide (mobile browsers)</li>
            <li>Online/offline status changes</li>
            <li>User activity detection and timer reset</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
